/**
 * MyClips V3 — VERSİYONLU RENDITION LADDER SÖZLEŞMESİ (P0.4, 2026-09-05)
 *
 * ── NEDEN VAR (ölçülen kusur) ─────────────────────────────────────────────
 * Üç ayrı yerde üç ayrı "ladder" yaşıyordu ve hiçbiri diğerini ölçmüyordu:
 *   · istemci  `useVariantSelector` / `QualityToggle`  → 480 / 720 / **1080**
 *   · functions `myclips_v3_transcode.ts VARIANT_LADDER` → 240 / 480 / 720
 *     (hiçbir yerden okunmuyordu — ölü sabit)
 *   · Cloud Run `cloud-run-transcoder/lib/ffmpegArgs.js LADDER` → 240 / 480 / 720
 *     (**tek gerçek üretici**: ffmpeg çıktısı + `v3_variants[]` buradan yazılır)
 * Sonuç: menü 1080p vaat ediyordu, sunucu hiç üretmiyordu. Bu dosya TEK
 * kaynaktır; diğer iki kopya (`functions/src/shared/ladder.ts`,
 * `cloud-run-transcoder/lib/ladder.js`) AST sözleşme testiyle buna eşitlenir
 * (`src/myclipsv3/shared/ladder.sozlesme.test.ts`). Paket sınırları
 * (CRA `src/` kapsamı · functions `rootDir: src` · Docker yalnız `lib/`
 * kopyalar) tek dosyayı üç paketten import etmeye izin vermiyor; bu yüzden
 * "üç kopya + eşitlik kapısı" deseni (emsal: `--space-*`/`--mlv3-space-*`).
 *
 * ── DEĞİŞMEZLER ──────────────────────────────────────────────────────────
 *   · `res` = KISA KENAR (dikey 9:16 klipte GENİŞLİK). `480p` = 480×854.
 *     Bu, ffmpeg `scale=<res>:-2`, R2 dizin adı `<res>p/` ve `v3_variants[].res`
 *     ile aynı birimdir. `height`, master.m3u8 RESOLUTION'ının yazdığı dikey
 *     yüksekliktir (`2·round(res·16/9/2)`) — hls.js `level.height` bunu döner.
 *   · Sıra DÜŞÜK→YÜKSEK (master playlist ve `var_stream_map` bu sırayı izler;
 *     kilitli değişmez #8 `startLevel: 0` ilk fragmanı ilk kademeden alır).
 *   · `secilebilir: false` kademe (240p) yalnız ABR/instant-start tabanıdır;
 *     kullanıcı menüsünde GÖRÜNMEZ (mevcut UX korunur).
 *   · Kademe eklemek/çıkarmak = `LADDER_VERSION` artar ve ÜÇ kopya birden
 *     değişir; tek kopyayı değiştiren kapı kırmızı yer.
 *   · Klip dokümanı: `v3_variants[]` (üretilen renditions — alan ZATEN vardı,
 *     yeniden icat edilmedi) + `v3_ladderVersion` (Cloud Run yazar,
 *     `firestore.rules` server-only). Eski kliplerde `v3_ladderVersion` YOK →
 *     istemci kademeleri `v3_variants[].res`ten türetir (geriye uyumlu).
 */

export type LadderRung = {
  /** Kullanıcıya görünen ad ("480p"). */
  ad: string;
  /** Kısa kenar (dikey klipte genişlik). `v3_variants[].res` ile aynı birim. */
  res: number;
  /** ffmpeg `scale=<width>:-2`. */
  width: number;
  /** master.m3u8 RESOLUTION dikey yüksekliği (9:16). hls.js `level.height`. */
  height: number;
  /** libx264 `-b:v` hedef ortalama. */
  bitrateKbps: number;
  /** `-maxrate` (%10 tavan payı). */
  maxrateKbps: number;
  /** `-bufsize` (2× bitrate). */
  bufsizeKbps: number;
  /** AAC-LC `-b:a` (tek ses rendition'ı, ses ABR yok). */
  audioKbps: number;
  /** Kalite menüsünde kullanıcıya sunulur mu. */
  secilebilir: boolean;
};

/** Ladder sözleşme sürümü — kademe listesi değişince ARTAR (üç kopyada da). */
export const LADDER_VERSION = 1;

export const LADDER: readonly LadderRung[] = Object.freeze([
  {
    ad: "240p",
    res: 240,
    width: 240,
    height: 426,
    bitrateKbps: 250,
    maxrateKbps: 275,
    bufsizeKbps: 500,
    audioKbps: 96,
    secilebilir: false,
  },
  {
    ad: "480p",
    res: 480,
    width: 480,
    height: 854,
    bitrateKbps: 600,
    maxrateKbps: 660,
    bufsizeKbps: 1200,
    audioKbps: 96,
    secilebilir: true,
  },
  {
    ad: "720p",
    res: 720,
    width: 720,
    height: 1280,
    bitrateKbps: 1500,
    maxrateKbps: 1650,
    bufsizeKbps: 3000,
    audioKbps: 96,
    secilebilir: true,
  },
]);

/** Kullanıcı menüsünde sunulan kademeler (düşük→yüksek). */
export const SECILEBILIR_KADEMELER: readonly LadderRung[] = Object.freeze(
  LADDER.filter((r) => r.secilebilir),
);

/** `localStorage["mlv3.player.quality"]` için geçerli manuel değerler ("480", "720"). */
export const MANUEL_KALITE_DEGERLERI: readonly string[] = Object.freeze(
  SECILEBILIR_KADEMELER.map((r) => String(r.res)),
);

/** `res` → kademe; ladder dışı bir değer için `null`. */
export function kademeBul(res: unknown): LadderRung | null {
  const n = Number(res);
  if (!Number.isFinite(n)) return null;
  return LADDER.find((r) => r.res === n) || null;
}

/** Kullanıcıya görünen etiket; ladder dışı (eski/yabancı) değer için `${res}p`. */
export function kademeEtiketi(res: unknown): string {
  const k = kademeBul(res);
  if (k) return k.ad;
  const n = Number(res);
  return Number.isFinite(n) && n > 0 ? `${n}p` : "Auto";
}

/**
 * Klip dokümanındaki `v3_variants[]`ten (istemci `clip.variants`) mevcut
 * `res` listesini türetir — düşük→yüksek, tekilleştirilmiş. Geriye uyumluluk:
 * `v3_ladderVersion` olmayan eski klipler de bu yoldan okunur.
 */
export function varyantlardanKademeler(
  variants: ReadonlyArray<{ res?: unknown } | null | undefined> | null | undefined,
): number[] {
  if (!Array.isArray(variants)) return [];
  const out = new Set<number>();
  for (const v of variants) {
    const n = Number(v?.res);
    if (Number.isFinite(n) && n > 0) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * Menüde gösterilecek kademeler: klipte GERÇEKTEN var olan (`available`)
 * ∩ ladder'da `secilebilir` olan. Ladder'da olmayan bir `res` (ör. hls.js'in
 * döndürdüğü 854/1280 yükseklikleri ya da yabancı bir 1080) GÖSTERİLMEZ —
 * gösterilse seçimi `setQualityPreference` reddederdi (sessiz yalan).
 * Sıra: yüksek→düşük (menü düzeni).
 */
export function menuKademeleri(available: ReadonlyArray<unknown> | null | undefined): LadderRung[] {
  if (!Array.isArray(available)) return [];
  const mevcut = new Set(available.map((r) => Number(r)).filter((n) => Number.isFinite(n)));
  return SECILEBILIR_KADEMELER.filter((r) => mevcut.has(r.res))
    .slice()
    .sort((a, b) => b.res - a.res);
}
